import axios from 'axios';
import AuthService from './AuthService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests.
// The app has two token storage paths:
//   1. Redux login  → localStorage key 'streamToken'
//   2. OTP/phone login → localStorage key 'userDetails' (JSON, read via AuthService.getToken())
// We check both so either login method works.
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('streamToken') || AuthService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class TicketService {
  // Get ticket metadata (categories, priorities, statuses)
  async getMetadata() {
    try {
      const response = await apiClient.get('/tickets/metadata');
      return response.data;
    } catch (error) {
      console.error('Error fetching ticket metadata:', error);
      throw error;
    }
  }

  // Get user's tickets
  async getMyTickets(status = null) {
    try {
      const params = status ? { status } : {};
      const response = await apiClient.get('/tickets/my-tickets', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching tickets:', error);
      throw error;
    }
  }

  // Get user's ticket statistics
  async getMyStats() {
    try {
      const response = await apiClient.get('/tickets/my-stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching ticket stats:', error);
      throw error;
    }
  }

  // Get single ticket
  async getTicket(id) {
    try {
      const response = await apiClient.get(`/tickets/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }
  }

  // Create new ticket
  async createTicket(ticketData) {
    try {
      const response = await apiClient.post('/tickets', ticketData);
      return response.data;
    } catch (error) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  }

  // Update ticket
  async updateTicket(id, updates) {
    try {
      const response = await apiClient.put(`/tickets/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating ticket:', error);
      throw error;
    }
  }

  // Add reply to ticket
  async addReply(ticketId, message, isInternal = false) {
    try {
      const response = await apiClient.post(`/tickets/${ticketId}/replies`, {
        message,
        is_internal: isInternal
      });
      return response.data;
    } catch (error) {
      console.error('Error adding reply:', error);
      throw error;
    }
  }

  // Admin: Get all tickets
  async getAllTickets(filters = {}) {
    try {
      const response = await apiClient.get('/tickets/admin/all', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching all tickets:', error);
      throw error;
    }
  }

  // Admin: Get statistics
  async getAdminStats() {
    try {
      const response = await apiClient.get('/tickets/admin/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      throw error;
    }
  }

  // Admin: Update ticket (status, assignment, etc.)
  async adminUpdateTicket(id, updates) {
    try {
      const response = await apiClient.put(`/tickets/admin/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating ticket:', error);
      throw error;
    }
  }

  // Admin: Delete ticket
  async deleteTicket(id) {
    try {
      const response = await apiClient.delete(`/tickets/admin/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting ticket:', error);
      throw error;
    }
  }
}

export default new TicketService();
